import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Zap, Layers, Clock, Check, ArrowRight, ExternalLink } from 'lucide-react';
import { TECHNOLOGIES } from '../mock';
import { useBooking } from '../context/BookingContext';

// Depth bar visualization per technology
const DepthBar = ({ depths, active }) => {
  const skinLayers = [
    { name: 'Epidermis', bg: 'bg-rose-50', height: 'h-6' },
    { name: 'Dermis', bg: 'bg-rose-100', height: 'h-10' },
    { name: 'Deep Dermis', bg: 'bg-rose-200', height: 'h-10' },
    { name: 'SMAS', bg: 'bg-stone-200', height: 'h-8' },
    { name: 'Muscle', bg: 'bg-stone-300', height: 'h-6' },
  ];

  return (
    <div className="flex gap-5 items-end">
      {/* Skin layer diagram */}
      <div className="flex flex-col gap-px w-28 flex-shrink-0">
        {skinLayers.map((layer) => (
          <div key={layer.name} className={`${layer.height} ${layer.bg} flex items-center pl-2`}>
            <span className="text-[9px] text-stone-500 tracking-wide">{layer.name}</span>
          </div>
        ))}
        <div className="text-[9px] text-stone-400 mt-1 text-center">Skin Layers</div>
      </div>

      {/* Depth targets */}
      <div className="flex gap-3">
        {depths.map((d, i) => (
          <div key={i} className={`flex flex-col items-center transition-all duration-300 ${active ? 'opacity-100' : 'opacity-40'}`}>
            <div className="text-[10px] text-stone-500 mb-1 tracking-wide">{d.label}</div>
            <div
              className="w-8 bg-rose-400/80 rounded-sm transition-all duration-700"
              style={{
                height: `${20 + i * 22}px`,
                backgroundColor: i === 0 ? '#C4897A' : i === 1 ? '#B5935A' : '#8B6F5E',
                transitionDelay: `${i * 100}ms`
              }}
            />
            <div className="text-[10px] text-stone-400 mt-1 font-medium">{d.depth}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Comparison table
const ComparisonTable = ({ active, onSelect }) => {
  const cols = [
    { key: 'energy', label: 'Energy Type' },
    { key: 'target', label: 'Target Depth' },
    { key: 'downtime', label: 'Downtime' },
    { key: 'sessions', label: 'Sessions' },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-stone-200">
            <th className="text-left py-3 pr-4 text-stone-500 text-xs tracking-widest uppercase font-light w-32">Technology</th>
            {cols.map(c => (
              <th key={c.key} className="text-left py-3 px-3 text-stone-500 text-xs tracking-widest uppercase font-light">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TECHNOLOGIES.map((tech) => (
            <tr
              key={tech.id}
              onClick={() => onSelect(tech.id)}
              className={`border-b cursor-pointer transition-colors duration-200 ${
                active === tech.id ? 'border-rose-200 bg-rose-50/60' : 'border-stone-100 hover:bg-stone-50'
              }`}
            >
              <td className="py-3 pr-4">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${active === tech.id ? 'bg-rose-400' : 'bg-stone-300'}`} />
                  <span className={`font-semibold text-sm ${active === tech.id ? 'text-stone-800' : 'text-stone-600'}`}>{tech.name}</span>
                </div>
                <div className="text-[10px] text-stone-400 ml-4 mt-0.5">{tech.tagline}</div>
              </td>
              {cols.map(c => (
                <td key={c.key} className="py-3 px-3 text-stone-500 text-sm font-light">{tech[c.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Technology = () => {
  const { openBooking } = useBooking();
  const [active, setActive] = useState('xerf');
  const [tab, setTab] = useState('overview'); // overview | comparison
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const tech = TECHNOLOGIES.find(t => t.id === active);

  return (
    <section id="technology" ref={ref} className="py-28 bg-[#FDF8F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Section header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-rose-400" />
            <span className="text-rose-400 text-xs tracking-[0.4em] uppercase font-light">Advanced Technology</span>
            <div className="w-8 h-px bg-rose-400" />
          </div>
          <h2
            className="text-stone-800 mb-4"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)' }}
          >
            The Science Behind Your Results
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto font-light text-lg leading-relaxed">
            We invest in the most advanced, clinically proven technologies — each targeting a different layer of skin
            for transformative, long-lasting results.
          </p>

          {/* Tab toggle */}
          <div className="flex justify-center gap-0 mt-8">
            {['overview', 'comparison'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`text-xs tracking-widest uppercase px-8 py-2.5 border transition-all duration-300 ${
                  tab === t
                    ? 'bg-stone-800 text-white border-stone-800'
                    : 'border-stone-200 text-stone-500 hover:border-stone-400'
                }`}
              >
                {t === 'overview' ? 'Technology Deep Dive' : 'Compare Them'}
              </button>
            ))}
          </div>
        </div>

        {/* OVERVIEW TAB */}
        {tab === 'overview' && (
          <div
            className={`transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Tech Selector */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 max-w-3xl mx-auto">
              {TECHNOLOGIES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  className={`p-5 border text-left transition-all duration-300 group ${
                    active === t.id
                      ? 'border-rose-400 bg-white shadow-md'
                      : 'border-stone-200 bg-white hover:border-rose-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className={`text-lg font-semibold transition-colors duration-300 ${active === t.id ? 'text-stone-800' : 'text-stone-600'}`}
                        style={{ fontFamily: "'Playfair Display', serif" }}>
                        {t.name}
                      </div>
                      <div className="text-stone-400 text-xs font-light mt-0.5">{t.brand}</div>
                    </div>
                    <span className={`text-[9px] tracking-widest uppercase px-2 py-1 ${
                      active === t.id ? 'bg-rose-100 text-rose-500' : 'bg-stone-100 text-stone-400'
                    }`}>
                      {t.badge}
                    </span>
                  </div>
                  <div className={`text-xs font-light leading-relaxed transition-colors duration-300 ${active === t.id ? 'text-stone-500' : 'text-stone-400'}`}>
                    {t.tagline}
                  </div>
                  {active === t.id && (
                    <div className="w-6 h-0.5 bg-rose-400 mt-3" />
                  )}
                </button>
              ))}
            </div>

            {/* Active Tech Detail */}
            {tech && (
              <div className="grid lg:grid-cols-2 gap-10 bg-white border border-stone-100 shadow-sm">

                {/* LEFT: Info */}
                <div className="p-8 lg:p-10">
                  <div className="inline-block bg-rose-50 text-rose-500 text-[10px] tracking-widest uppercase px-3 py-1 mb-4">
                    {tech.type}
                  </div>
                  <h3
                    className="text-stone-800 text-2xl mb-3 leading-snug"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {tech.headline}
                  </h3>
                  <p className="text-stone-500 font-light leading-relaxed mb-7 text-sm">
                    {tech.description}
                  </p>

                  {/* Mechanism */}
                  <div className="mb-7">
                    <div className="text-stone-700 text-xs tracking-widest uppercase mb-3 flex items-center gap-2">
                      <Zap className="w-3.5 h-3.5 text-rose-400" />
                      How It Works
                    </div>
                    <ul className="space-y-2">
                      {tech.mechanism.map((m, i) => (
                        <li key={i} className="flex items-start gap-2 text-stone-500 text-sm font-light">
                          <div className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0 mt-1.5" />
                          {m}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Meta */}
                  <div className="grid grid-cols-3 gap-3 mb-7">
                    {[
                      { icon: Clock, label: 'Sessions', value: tech.sessions },
                      { icon: Layers, label: 'Downtime', value: tech.downtime },
                      { icon: Check, label: 'Best For', value: tech.bestFor },
                    ].map((item, i) => (
                      <div key={i} className="bg-stone-50 p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <item.icon className="w-3.5 h-3.5 text-rose-400" />
                          <span className="text-[10px] tracking-widest uppercase text-stone-400">{item.label}</span>
                        </div>
                        <div className="text-stone-700 text-xs font-medium">{item.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Depth viz */}
                  <div className="mb-7">
                    <div className="text-stone-700 text-xs tracking-widest uppercase mb-3 flex items-center gap-2">
                      <Layers className="w-3.5 h-3.5 text-rose-400" />
                      Treatment Depths
                    </div>
                    <DepthBar depths={tech.depths} active={true} />
                  </div>

                  <button
                    onClick={() => openBooking()}
                    className="group flex items-center gap-2 bg-stone-800 hover:bg-rose-400 text-white text-xs tracking-widest uppercase px-8 py-3 transition-all duration-300"
                  >
                    Book {tech.name}
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>

                {/* RIGHT: Image + Benefits + Before/After */}
                <div className="flex flex-col">
                  {/* Main image */}
                  <div className="relative overflow-hidden h-64 lg:h-72">
                    <img
                      src={tech.image}
                      alt={tech.name}
                      className="w-full h-full object-cover"
                      key={tech.id}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {tech.tagline}
                      </div>
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="p-6 flex-1">
                    <div className="text-stone-700 text-xs tracking-widest uppercase mb-4 flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-rose-400" />
                      Key Benefits
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {tech.benefits.map((b, i) => (
                        <div key={i} className="flex items-center gap-2 text-stone-500 text-xs font-light">
                          <div className="w-1.5 h-1.5 rounded-full bg-rose-300 flex-shrink-0" />
                          {b}
                        </div>
                      ))}
                    </div>

                    {/* XERF before/after if available */}
                    {tech.beforeAfter && (
                      <div>
                        <div className="text-stone-700 text-xs tracking-widest uppercase mb-3">Clinical Results</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <img
                              src={tech.beforeAfter.before}
                              alt="Before"
                              className="w-full h-28 object-cover object-top"
                            />
                            <div className="text-[9px] text-stone-400 mt-1 text-center tracking-wide">BEFORE</div>
                          </div>
                          <div>
                            <img
                              src={tech.beforeAfter.after}
                              alt="After"
                              className="w-full h-28 object-cover object-top"
                            />
                            <div className="text-[9px] text-stone-400 mt-1 text-center tracking-wide">AFTER</div>
                          </div>
                        </div>
                        <div className="text-[9px] text-stone-400 mt-2 italic leading-relaxed">{tech.beforeAfter.credit}</div>
                      </div>
                    )}

                    {tech.id === 'xerf' && (
                      <a
                        href="https://www.cynosure.com/xerf/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-rose-400 transition-colors duration-200"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Learn more at Cynosure.com
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* COMPARISON TAB */}
        {tab === 'comparison' && (
          <div
            className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Visual comparison bars */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10 max-w-3xl mx-auto">
              {TECHNOLOGIES.map((t) => (
                <div
                  key={t.id}
                  onClick={() => { setActive(t.id); setTab('overview'); }}
                  className="bg-white border border-stone-100 hover:border-rose-300 hover:shadow-sm cursor-pointer transition-all duration-300 group"
                >
                  {/* Color stripe */}
                  <div
                    className="h-1.5 w-full"
                    style={{ backgroundColor: t.accent }}
                  />
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-stone-800 font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</div>
                        <div className="text-stone-400 text-xs mt-0.5">{t.tagline}</div>
                      </div>
                      <span className="text-[9px] bg-stone-100 text-stone-500 tracking-widest uppercase px-2 py-1">{t.badge}</span>
                    </div>

                    {/* Attribute bars */}
                    {[
                      { label: 'Depth', value: t.id === 'xerf' ? 80 : 40 },
                      { label: 'Comfort', value: t.id === 'xerf' ? 95 : t.id === 'microneedling' ? 70 : 60 },
                      { label: 'Downtime', value: t.id === 'xerf' ? 5 : 25, invert: true },
                      { label: 'Results Speed', value: t.id === 'microneedling' ? 70 : t.id === 'xerf' ? 75 : 50 },
                    ].map((attr) => (
                      <div key={attr.label} className="mb-2">
                        <div className="flex justify-between text-[10px] text-stone-400 mb-1">
                          <span>{attr.label}</span>
                          <span>{attr.invert ? (100 - attr.value) + '%' : attr.value + '%'}</span>
                        </div>
                        <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: `${attr.invert ? 100 - attr.value : attr.value}%`,
                              backgroundColor: t.accent
                            }}
                          />
                        </div>
                      </div>
                    ))}

                    <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-end">
                      <div className="flex items-center gap-1 text-stone-400 text-xs group-hover:text-rose-400 transition-colors duration-200">
                        Details <ChevronRight className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Data table */}
            <div className="bg-white border border-stone-100 p-6 shadow-sm">
              <div className="text-stone-700 text-xs tracking-widest uppercase mb-5">Full Comparison Table</div>
              <ComparisonTable active={active} onSelect={(id) => { setActive(id); setTab('overview'); }} />
            </div>

            {/* Combined treatment callout */}
            <div className="mt-8 bg-stone-800 p-8 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-rose-400 text-xs tracking-widest uppercase mb-3">Clinical Best Practice</div>
                <h3
                  className="text-white text-xl mb-3 leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  The Multi-Layer Approach
                </h3>
                <p className="text-stone-400 font-light text-sm leading-relaxed">
                  Dermatology consensus combines RF tightening, microneedling, and ultrasound because
                  each targets a different skin layer. At Akira, Angela Trias designs personalized
                  protocols that layer these technologies for maximum, synergistic results.
                </p>
              </div>
              <div>
                <div className="flex flex-col gap-3">
                  {[
                    { step: '01', label: 'RF Tightening (XERF)', sub: 'Deep dermis → SMAS coagulation', color: '#C4897A' },
                    { step: '02', label: 'Microneedling', sub: 'Collagen induction + serum infusion', color: '#B5935A' },
                    { step: '03', label: 'RF Microneedling (optional)', sub: 'Deeper dermal remodeling for laxity', color: '#8B6F5E' },
                  ].map((item) => (
                    <div key={item.step} className="flex items-center gap-4">
                      <div
                        className="w-8 h-8 flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.step}
                      </div>
                      <div>
                        <div className="text-white text-sm font-medium">{item.label}</div>
                        <div className="text-stone-400 text-xs font-light">{item.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => openBooking()}
                  className="mt-6 group bg-rose-400 hover:bg-rose-500 text-white text-xs tracking-widest uppercase px-8 py-3 transition-all duration-300 flex items-center gap-2"
                >
                  Book a Consultation
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Technology;
